/**
 * Links into the public marketing site (sendtoclara.ai) — legal and support
 * pages live there, not in the app (issue #52; routes from marketing/src/lib/legal.ts).
 *
 * `VITE_MARKETING_URL` lets a non-production lane (e.g. staging, issue #285)
 * point at its own marketing origin without a code change.
 *
 * The default below matches the live Clara domain (domain cutover completed
 * 2026-07, issue #522). `VITE_MARKETING_URL` remains an override for
 * non-production lanes — it is no longer the cutover mechanism.
 */

const MARKETING_URL: string = import.meta.env.VITE_MARKETING_URL ?? 'https://sendtoclara.ai'

export const PRIVACY_URL = `${MARKETING_URL}/legal/privacy`
export const TERMS_URL = `${MARKETING_URL}/legal/terms`
export const SUPPORT_URL = `${MARKETING_URL}/support`

/**
 * Invite-only gate (issue #515). The app signup page links out to the
 * marketing site's request-invite form; the marketing site is locale-routed
 * (EN at `/`, PT at `/pt/`) so the link must follow the app's active locale.
 * Both follow `VITE_MARKETING_URL` the same way PRIVACY_URL/TERMS_URL/SUPPORT_URL
 * do — no separate env var.
 */
export const REQUEST_INVITE_URL = `${MARKETING_URL}/request-invite`
export const REQUEST_INVITE_URL_PT = `${MARKETING_URL}/pt/request-invite`

/**
 * Domain for public contact addresses. Split from MARKETING_URL because the two
 * can move independently: mail delivery needs MX records, which can lag a
 * plain site domain move. MX for the domain below is live (domain cutover
 * completed 2026-07, issue #522).
 */
const CONTACT_EMAIL_DOMAIN: string =
  import.meta.env.VITE_CONTACT_EMAIL_DOMAIN ?? 'sendtoclara.ai'

export const ENTERPRISE_EMAIL = `enterprise@${CONTACT_EMAIL_DOMAIN}`

/**
 * Locale-aware marketing landing URL (logged-out Clara logo target, issue
 * #522). Mirrors the REQUEST_INVITE_URL_PT pattern: the marketing site is
 * locale-routed with PT under /pt.
 */
export function marketingHomeUrl(resolvedLanguage: string | undefined): string {
  return resolvedLanguage === 'pt-PT' ? `${MARKETING_URL}/pt` : MARKETING_URL
}
